import { Component, EventEmitter, Inject, Input, KeyValueDiffers, OnInit, Output } from '@angular/core';
import { ExtraFeeModel } from './model/basket.fee.model';
import { OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { BasketPageModel } from 'libs/core/src/lib/model/page/basket/basket.page.model';
import { storageKey } from 'libs/core/src/app.const';
import { ScreeningItemViewModel } from 'libs/core/src/lib/model/view-model/order/screening-item/screening-item.view.model';
import { ENVIRONMENT_TOKEN, OrderStateService } from 'libs/core/src/public-api';
import { StateService } from 'libs/core/src/lib/state/state.service';
import { TicketExtraFeeViewModel } from 'libs/core/src/lib/model/view-model/shared/ticket/ticket-extra-fee.view.model';
import { TicketExtraFeeApiModel } from 'libs/core/src/lib/model/api-model/shared/ticket/ticket-extra-fee.api.model';
import { tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  template: ''
})
export class BasketFeeComponent implements OnInit {
  @Input() public basketPageModel: BasketPageModel;
  public order: OrderViewModel = null;

  @Input() public allowedTags: string[] = [];

  @Output() hasRestrictedBasketExtraFeesChange = new EventEmitter<boolean>();

  public basketExtraFees: ExtraFeeModel[] = [];
  public restrictedBasketExtraFees: ExtraFeeModel[] = [];

  constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    protected serviceDiffers: KeyValueDiffers,
    protected stateService: StateService,
    protected orderState: OrderStateService
  ) {}

  ngOnInit() {
    this.orderState.state$
      .pipe(
        tap((o) => {
          this.order = o;

          this.applyChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  optionalExtraFeesSelection() {
    if (
      this.basketExtraFees &&
      this.environment.pages.optionalExtraFeeSelection &&
      this.environment.pages.optionalExtraFeeSelection === 'all' &&
      this.stateService.getItem(storageKey.isExtraFeesSelected) !== 'true'
    ) {
      this.basketExtraFees.forEach((fee) => {
        for (let i = 0; i < fee.limit; i++) {
          this.doIncrement(fee);
        }
      });
    }

    this.order.screeningItems.forEach((item) => {
      if (item.ticketId && !this.basketExtraFees.find((fee) => fee.ticketIds.includes(item.ticketId))) {
        item.optionalExtraFees = [];
      }
    });
  }

  protected applyChanges() {
    const tickets = this.basketPageModel?.tickets ?? [];
    const basketExtraFees: ExtraFeeModel[] = [];
    const orderTicketsChosen = this.order?.screeningItems.map((x) => ({ ticketId: x.ticketId, screeningItemId: x.id }));
    const ticketsChosen = tickets.filter(
      (f) => orderTicketsChosen?.find((ticket) => ticket.screeningItemId === f.screeningItemId && ticket.ticketId === f.id) !== undefined
    );

    let ticketIdAndExtraFeeModel = ticketsChosen?.map((ticket) => ({
      screeningItemId: ticket.screeningItemId,
      ticketId: ticket.id,
      extraFees: ticket.extraFees.filter((ef) => ef.isOptional)
    }));

    this.order?.screeningItems
      .map((screeningItem) => ({
        screeningItemId: screeningItem.id,
        ticketId: screeningItem.ticketId,
        extraFees: screeningItem.extraFees
          .map(
            (m) =>
              new TicketExtraFeeViewModel({
                extraFeeId: m.id,
                isOptional: m.isOptional,
                defaultPriceLevelPrice: m.defaultPriceLevelPrice,
                name: m.name,
                price: m.price
              } as TicketExtraFeeApiModel)
          )
          .filter((ef) => ef.isOptional)
      }))
      .forEach((s) => {
        if (!ticketIdAndExtraFeeModel.some((t) => t.screeningItemId === s.screeningItemId)) {
          ticketIdAndExtraFeeModel.push(s);
        }
      });

    ticketIdAndExtraFeeModel?.forEach((elem) => {
      elem.extraFees.forEach((extraFee) => {
        const extraFeeInBasket = basketExtraFees.find((x) => x.id === extraFee.extraFeeId);
        if (extraFeeInBasket === undefined) {
          const item = new ExtraFeeModel({
            id: extraFee.extraFeeId,
            name: extraFee.name,
            price: extraFee.price,
            ticketIds: [elem.ticketId],
            tag: extraFee.tag,
            limit: 1
          });
          basketExtraFees.push(item);
        } else {
          extraFeeInBasket.limit += 1;
          extraFeeInBasket.ticketIds.push(elem.ticketId);
        }
      });
    });

    this.basketExtraFees = this.modifyBasketExtraFees(basketExtraFees);
    this.optionalExtraFeesSelection();
    this.restrictedBasketExtraFees = this.basketExtraFees?.filter((item) => !this.allowedTags?.length || this.allowedTags.includes(item.tag));
    this.hasRestrictedBasketExtraFeesChange.emit(this.restrictedBasketExtraFees?.length > 0);
  }

  onQuantityChanged(event, extraFee) {
    if (event.diff > 0) {
      this.doIncrement(extraFee);
    } else if (event.diff < 0) {
      this.doDecrement(extraFee);
    }
  }

  doIncrement(extraFee: ExtraFeeModel) {
    const foundOrderItemModel = this.order.screeningItems.find((item: ScreeningItemViewModel) => {
      return extraFee.ticketIds.includes(item.ticketId) && !item.optionalExtraFees.includes(extraFee.id);
    });

    if (foundOrderItemModel !== undefined) {
      foundOrderItemModel.optionalExtraFees.push(extraFee.id);
    }

    this.orderState.setOrder(this.order);
  }

  doDecrement(extraFee: ExtraFeeModel) {
    const foundOrderItemModel = this.order.screeningItems.find((item: ScreeningItemViewModel) => {
      return extraFee.ticketIds.includes(item.ticketId) && item.optionalExtraFees.includes(extraFee.id);
    });

    if (foundOrderItemModel !== undefined) {
      const index = foundOrderItemModel.optionalExtraFees.indexOf(extraFee.id);
      if (index > -1) {
        foundOrderItemModel.optionalExtraFees.splice(index, 1);
      }
    }

    this.orderState.setOrder(this.order);
  }

  public getTotal() {
    return this.basketExtraFees.reduce((v, k) => {
      v += k.price * this.order.getCountOptionalFeeByGroupFee(k.id);
      return v;
    }, 0);
  }

  protected modifyBasketExtraFees(basketExtraFees) {
    return basketExtraFees;
  }
}
